import type { DataSource } from '../dataSource/dataSource'
import type { CreateTask, Task } from '../db/models'
import { cloneTaskFields } from './cloneTaskFields'

/**
 * Duplicates a task with everything that belongs to it.
 *
 * `cloneTaskFields` only ever sees task columns, so it cannot know about the
 * subtasks table. Every duplicate call site goes through here instead, for the
 * same reason `cloneTaskFields` exists in the first place: a new thing that
 * belongs to a task should have to be handled once, not at four call sites that
 * silently drift apart.
 *
 * `resetSubtasks` ist für den **Nachfolger** einer Wiederholung (ADR 0021): Die Liste ist
 * dieselbe, erledigt ist noch nichts. Duplizieren lässt die Haken, wie sie sind.
 * `shiftSubtaskDays`: Wann und Deadline der Unteraufgaben wandern im selben Abstand
 * wie die Runde (T14 #179).
 *
 * Geschrieben wird über `dataSource` (in Hooks: `useDataSource()`).
 *
 * @example
 * const copy = await duplicateTask(useDataSource(), task, { order: nextOrder })
 */
export async function duplicateTask(
  dataSource: Pick<DataSource, 'createTask' | 'copySubtasks' | 'copyReminders'>,
  source: Task,
  overrides?: Partial<CreateTask>,
  options: { resetSubtasks?: boolean; shiftSubtaskDays?: number } = {},
): Promise<Task> {
  const { copy, copied } = await duplicateTaskBranch(dataSource, source, overrides, options)
  // Die relativen Erinnerungen passen auch an der Kopie, samt Standard; feste gehören zum Original.
  await dataSource.copyReminders(new Map([[source.id, copy.id], ...copied]))
  return copy
}

/**
 * Wie {@link duplicateTask}, dazu `copied`: welche Unteraufgabe zu welcher Kopie wurde, über
 * alle Ebenen. Der Nachfolger einer Wiederholung hängt damit die Kommentare um (#182).
 * **Ohne Erinnerungen** — die holt der Aufrufer: die Kopie über `copyReminders`, der
 * Nachfolger über `moveReminders`. Sonst bekäme die neue Zeile zusätzlich die Standard-Erinnerung.
 */
export async function duplicateTaskBranch(
  dataSource: Pick<DataSource, 'createTask' | 'copySubtasks'>,
  source: Task,
  overrides?: Partial<CreateTask>,
  options: { resetSubtasks?: boolean; shiftSubtaskDays?: number } = {},
): Promise<{ copy: Task; copied: Map<string, string> }> {
  const copy = await dataSource.createTask({ ...cloneTaskFields(source, overrides), reminders: [] })
  const copied = await dataSource.copySubtasks(source.id, copy.id, { resetDone: options.resetSubtasks, shiftDays: options.shiftSubtaskDays })
  return { copy, copied }
}
